import { FC, useRef, useState } from 'react';
import {
    Alert,
    Avatar,
    Badge,
    CircularProgress,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Snackbar,
} from '@mui/material';
import {
    PhotoCamera as CameraIcon,
    Upload as UploadIcon,
    DeleteOutlined as RemoveIcon,
} from '@mui/icons-material';
import { useChangePictureMutation } from '@/store/apiSlice';
import { useI18nContext } from '@/i18n/i18n-react';
import { styleHelpers } from '@/theme';
import type { User } from '@/types/user';

/**
 * The user's avatar: their picture, or their initials. When the IdP lets this app change pictures, a camera
 * button uploads a new one or removes it.
 */
const ProfilePicture: FC<{ user: User; size?: number }> = ({ user, size = 72 }) => {
    const { LL } = useI18nContext();
    const fileInput = useRef<HTMLInputElement>(null);
    const [menu, setMenu] = useState<HTMLElement | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [changePicture, { isLoading }] = useChangePictureMutation();

    const send = async (body: FormData) => {
        setError(null);
        try {
            await changePicture(body).unwrap();
        } catch (e) {
            const status = (e as { status?: unknown }).status;
            setError(
                status === 400
                    ? LL.pictureInvalid()
                    : status === 403
                      ? LL.pictureNotAllowed()
                      : LL.pictureFailed()
            );
        }
    };

    const avatar = (
        // The initials stay as the fallback when there is no picture or it fails to load.
        <Avatar
            src={user.picture ?? undefined}
            alt=""
            sx={styleHelpers.roleAvatar(user.isAdmin, size)}
        >
            {user.displayName
                .split(' ')
                .map((e: string) => e.charAt(0).toUpperCase())
                .slice(0, 2)
                .join('')}
        </Avatar>
    );
    if (!user.canChangePicture) return avatar;

    return (
        <>
            <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                // The badge box is sized to the button, so it sits centred on the avatar's edge.
                sx={{
                    flexShrink: 0,
                    alignSelf: 'flex-start',
                    '& .MuiBadge-badge': { p: 0, width: 30, height: 30 },
                }}
                badgeContent={
                    <IconButton
                        size="small"
                        aria-label={LL.changePicture()}
                        aria-haspopup="menu"
                        disabled={isLoading}
                        onClick={(e) => setMenu(e.currentTarget)}
                        sx={(theme) => ({
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            color: 'text.primary',
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: theme.shadows[2],
                            '&:hover': { bgcolor: 'background.paper' },
                        })}
                    >
                        {isLoading ? (
                            <CircularProgress size={16} />
                        ) : (
                            <CameraIcon sx={{ fontSize: 17 }} />
                        )}
                    </IconButton>
                }
            >
                {avatar}
            </Badge>
            <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
                <MenuItem
                    onClick={() => {
                        setMenu(null);
                        fileInput.current?.click();
                    }}
                >
                    <ListItemIcon>
                        <UploadIcon fontSize="small" />
                    </ListItemIcon>
                    {LL.uploadPicture()}
                </MenuItem>
                {user.picture && (
                    <MenuItem
                        onClick={() => {
                            setMenu(null);
                            const body = new FormData();
                            body.append('remove', '1');
                            void send(body);
                        }}
                    >
                        <ListItemIcon>
                            <RemoveIcon fontSize="small" />
                        </ListItemIcon>
                        {LL.removePicture()}
                    </MenuItem>
                )}
            </Menu>
            <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                hidden
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    // Cleared so the same file can be chosen again after an error.
                    e.target.value = '';
                    if (!file) return;
                    const body = new FormData();
                    body.append('picture', file);
                    void send(body);
                }}
            />
            <Snackbar
                open={!!error}
                autoHideDuration={8000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" variant="filled" onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ProfilePicture;
